import type { FirewallRulesetIssue } from '@nadoutong/contracts';
import { isOneOf } from './isOneOf.ts';

/**
 * The editable form of one firewall rule (#1760).
 *
 * Why a draft type at all, rather than editing `FirewallRule` in place: a
 * stored version is IMMUTABLE. Nothing on this page mutates one — the operator
 * edits a local chain and submits it as a NEW draft version, and the only call
 * that changes production is the separate activate. Priority is the array
 * order, so "move this rule up" is a local array operation here and never an
 * API that reorders a live version.
 *
 * The rule vocabulary is restated here instead of imported from
 * `@nadoutong/firewall`: the console must not carry a build dependency on the
 * engine, and the wire contract already treats `rules` as opaque. The engine
 * stays authoritative — every write is validated against it server-side and the
 * returned `issues` are rendered as the answer. What is below is a pre-flight
 * that catches the four mistakes an operator makes at the keyboard before
 * spending a round trip on them, and it emits the ENGINE's issue codes and
 * `at` paths (`validate.ts`) so both sources render through one component.
 */

export const FIREWALL_RULE_ACTIONS = ['allow', 'deny', 'synthesize'] as const;
export type FirewallDraftAction = (typeof FIREWALL_RULE_ACTIONS)[number];

export const FIREWALL_DENY_AS = ['reject', 'model-unavailable'] as const;
export type FirewallDraftDenyAs = (typeof FIREWALL_DENY_AS)[number];

export const FIREWALL_SYNTHESIZE_AS = ['synthesize-200'] as const;
export type FirewallDraftSynthesizeAs = (typeof FIREWALL_SYNTHESIZE_AS)[number];

export interface FirewallRuleDraft {
  /**
   * Local identity, stable across reorder and rename. React keys and the
   * per-row controls hang off this rather than off `id`, so renaming a rule
   * mid-edit does not remount the row and lose focus.
   */
  readonly key: string;
  readonly id: string;
  readonly description: string;
  readonly action: FirewallDraftAction;
  readonly denyAs: FirewallDraftDenyAs;
  readonly synthesizeAs: FirewallDraftSynthesizeAs;
  /** The `when` expression as JSON source. Parsed on submit, not on keystroke. */
  readonly whenText: string;
  /**
   * Fields the console does not model, carried through untouched.
   *
   * A version authored by a future console (or by hand) must survive a
   * round trip through this editor. Dropping unknown keys would silently
   * rewrite someone else's rule while the operator was only moving it up one
   * position.
   */
  readonly extra: Readonly<Record<string, unknown>>;
}

const DEFAULT_WHEN_TEXT = '{\n  "op": "and",\n  "args": []\n}';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function nestedAs<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T,
): T {
  if (!isRecord(value)) return fallback;
  return isOneOf(value.as, allowed) ? value.as : fallback;
}

function draftKey(index: number): string {
  return `rule-${String(index)}`;
}

/**
 * Read a stored `rules` document into drafts, or `null` when it is not a shape
 * this editor can round-trip.
 *
 * `null` rather than a best-effort repair: an unreadable document still has an
 * active version serving traffic, and guessing at it would let a save replace
 * a live chain with the editor's guess. The caller shows it read-only instead.
 */
export function firewallRuleDraftsFromRules(rules: unknown): readonly FirewallRuleDraft[] | null {
  if (!Array.isArray(rules)) return null;
  const drafts: FirewallRuleDraft[] = [];
  for (const [index, rule] of rules.entries()) {
    if (!isRecord(rule)) return null;
    const { id, description, action, deny, synthesize, when, ...extra } = rule;
    drafts.push({
      key: draftKey(index),
      id: typeof id === 'string' ? id : '',
      description: typeof description === 'string' ? description : '',
      action: isOneOf(action, FIREWALL_RULE_ACTIONS) ? action : 'deny',
      denyAs: nestedAs(deny, FIREWALL_DENY_AS, 'reject'),
      synthesizeAs: nestedAs(synthesize, FIREWALL_SYNTHESIZE_AS, 'synthesize-200'),
      whenText: when === undefined ? DEFAULT_WHEN_TEXT : JSON.stringify(when, null, 2),
      extra,
    });
  }
  return drafts;
}

/** A key no draft in `drafts` is using. */
function freshKey(drafts: readonly FirewallRuleDraft[]): string {
  const used = new Set(drafts.map((draft) => draft.key));
  let index = drafts.length;
  while (used.has(draftKey(index))) index += 1;
  return draftKey(index);
}

export function appendRuleDraft(
  drafts: readonly FirewallRuleDraft[],
): readonly FirewallRuleDraft[] {
  return [
    ...drafts,
    {
      key: freshKey(drafts),
      id: '',
      description: '',
      action: 'deny',
      denyAs: 'reject',
      synthesizeAs: 'synthesize-200',
      whenText: DEFAULT_WHEN_TEXT,
      extra: {},
    },
  ];
}

export function updateRuleDraft(
  drafts: readonly FirewallRuleDraft[],
  key: string,
  patch: Partial<Omit<FirewallRuleDraft, 'key' | 'extra'>>,
): readonly FirewallRuleDraft[] {
  return drafts.map((draft) => (draft.key === key ? { ...draft, ...patch } : draft));
}

export function removeRuleDraft(
  drafts: readonly FirewallRuleDraft[],
  key: string,
): readonly FirewallRuleDraft[] {
  return drafts.filter((draft) => draft.key !== key);
}

/**
 * Move one rule one position, or return the same array when it cannot move.
 *
 * Returning the input unchanged at the ends (rather than wrapping around) is
 * what lets the caller disable the button by identity: first row cannot go up,
 * last row cannot go down, and priority is first-match-wins order.
 */
export function moveRuleDraft(
  drafts: readonly FirewallRuleDraft[],
  key: string,
  direction: 'up' | 'down',
): readonly FirewallRuleDraft[] {
  const from = drafts.findIndex((draft) => draft.key === key);
  if (from === -1) return drafts;
  const to = direction === 'up' ? from - 1 : from + 1;
  if (to < 0 || to >= drafts.length) return drafts;
  const moving = drafts[from];
  const displaced = drafts[to];
  if (moving === undefined || displaced === undefined) return drafts;
  return drafts.map((draft, index) => {
    if (index === to) return moving;
    if (index === from) return displaced;
    return draft;
  });
}

/**
 * Pre-flight validation, in the engine's own issue vocabulary.
 *
 * Deliberately a SUBSET of `validateFirewallRuleset`: it does not know the
 * operator table, the nesting budget or the chain the ruleset extends into, so
 * it never reports a rule as good — only as bad for one of the reasons that can
 * be seen locally. Everything else is the server's answer.
 */
export function validateRuleDrafts(
  drafts: readonly FirewallRuleDraft[],
): readonly FirewallRulesetIssue[] {
  const issues: FirewallRulesetIssue[] = [];
  const seen = new Set<string>();
  drafts.forEach((draft, index) => {
    const at = `rules[${String(index)}]`;
    const ruleId = draft.id.length > 0 ? { ruleId: draft.id } : {};
    if (draft.id.trim().length === 0) {
      issues.push({
        code: 'missing-rule-id',
        message: 'rule requires a non-empty "id"',
        at: `${at}.id`,
      });
    } else if (seen.has(draft.id)) {
      issues.push({
        code: 'duplicate-rule-id',
        message: `rule id ${JSON.stringify(draft.id)} appears more than once in this ruleset`,
        ...ruleId,
        at: `${at}.id`,
      });
    } else {
      seen.add(draft.id);
    }
    // #1757 keeps `description` out of the rule hash on purpose, which is
    // exactly why it has to be enforced here: nothing downstream will ever
    // reject a chain for being incomprehensible, and an undescribed rule is a
    // verdict nobody can explain six months later.
    if (draft.description.trim().length === 0) {
      issues.push({
        code: 'missing-description',
        message: 'rule requires a non-empty "description" saying what it is for',
        ...ruleId,
        at: `${at}.description`,
      });
    }
    if (parseWhenText(draft.whenText) === null) {
      issues.push({
        code: 'invalid-expression',
        message: 'rule "when" must be a JSON object describing a condition',
        ...ruleId,
        at: `${at}.when`,
      });
    }
  });
  return issues;
}

function parseWhenText(whenText: string): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(whenText);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export type SerializeRuleDraftsResult =
  | { readonly ok: true; readonly rules: readonly unknown[] }
  | { readonly ok: false; readonly issues: readonly FirewallRulesetIssue[] };

/**
 * The one way to turn drafts into a submittable document — validation is not
 * skippable, so an invalid chain has no serialized form to accidentally POST.
 */
export function rulesFromRuleDrafts(
  drafts: readonly FirewallRuleDraft[],
): SerializeRuleDraftsResult {
  const issues = validateRuleDrafts(drafts);
  if (issues.length > 0) return { ok: false, issues };
  const rules = drafts.map((draft) => ({
    ...draft.extra,
    id: draft.id.trim(),
    description: draft.description.trim(),
    action: draft.action,
    ...(draft.action === 'deny' ? { deny: { as: draft.denyAs } } : {}),
    ...(draft.action === 'synthesize' ? { synthesize: { as: draft.synthesizeAs } } : {}),
    when: parseWhenText(draft.whenText),
  }));
  return { ok: true, rules };
}
